import type { LinearRGB } from '../../../../core/color';
import type { Landmark } from '../../../../data/types';
import type { MeshBuilder, VertexStyle } from '../../../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../../../mesh/attributes';
import { meshFrameOf } from '../planFrame';
import { nieuweKerkFrame, planNieuweKerk } from './nieuweKerkPlan';
import type { NieuweKerkBuild } from './nieuweKerkParts';
import { buildVessel } from './nieuweKerkVessel';
import { buildTransept } from './nieuweKerkTransept';
import { buildAislesAndChapels } from './nieuweKerkAisles';
import { buildAmbulatory } from './nieuweKerkAmbulatory';
import { buildFleche } from './nieuweKerkFleche';
import type { TracerySink } from '../tracery/tracerySink';
import { LANDMARK_PATTERN, patterned, patternedWalls } from '../surfacePatterns';

/**
 * The share of the tower exaggeration the painter gave the Nieuwe Kerk: at 0.4 the painter view draws
 * the flèche 89.2 px, the ridge 71.1 px and the choir's eaves 44.8 px over the foot, as painted (89-91,
 * 72-74 and 44-46 px over a foot near frame row 606; docs/plans/landmarks.md, "Silhouettes"). And its
 * part of the landmark triangle budget.
 */
export const NIEUWE_KERK_STYLE = { share: 0.4, maxTriangles: 30_000 } as const;

export interface NieuweKerkColors {
  /** Brick banded with stone (De Gids 1942, p. 31), painted #645122 on the choir's clerestory. */
  readonly wall: LinearRGB;
  readonly slate: LinearRGB;
  readonly lead: LinearRGB;
  readonly dark: LinearRGB;
  readonly glass: LinearRGB;
  /** Stone jambs and tracery, as the Oude Kerk's (Janse 2004); the Nieuwe Kerk's of 1538 are not recorded. */
  readonly dressing: LinearRGB;
}

interface Ids {
  readonly edgeId: number;
  readonly seed: number;
}

function stylesOf(colors: NieuweKerkColors, ids: Ids, tracery: TracerySink) {
  const base = { ...ids, strokeAngle: 0, extent: [10, 10] as const, exaggeration: { base: 0, share: NIEUWE_KERK_STYLE.share } };
  const style = (color: LinearRGB, surface: SurfaceKind): VertexStyle => ({ ...base, color, surface });
  const glass = style(colors.glass, SURFACE.linework);
  const leaded = patterned(glass, LANDMARK_PATTERN.glass);
  const glazing = { reveal: style(colors.dressing, SURFACE.stone), tracery: { sink: tracery, edgeId: ids.edgeId }, leaded };
  const roof = patterned(style(colors.slate, SURFACE.roofSlate), LANDMARK_PATTERN.slate);
  const stone = { wall: style(colors.wall, SURFACE.brick), roof, opening: glass, glazing };
  return {
    stone,
    brick: patternedWalls(stone, LANDMARK_PATTERN.speklagen),
    lead: patterned(style(colors.lead, SURFACE.linework), LANDMARK_PATTERN.lead),
  };
}

/** The Nieuwe Kerk as it stood in 1538, its windows' tracery to `tracery`; returns the triangles it added. */
export function buildNieuweKerk(builder: MeshBuilder, landmark: Landmark, colors: NieuweKerkColors, ids: Ids, tracery: TracerySink): number {
  const plan = planNieuweKerk(nieuweKerkFrame(landmark));
  const build: NieuweKerkBuild = { builder, frame: meshFrameOf(plan.frame), plan, ...stylesOf(colors, ids, tracery) };
  const before = builder.triangleCount;
  buildVessel(build);
  buildTransept(build);
  buildAislesAndChapels(build);
  buildAmbulatory(build);
  buildFleche(build);
  return builder.triangleCount - before;
}
